import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Film, Download, Package, Loader2, Unlock, AlertTriangle, UploadCloud, ImagePlus } from "lucide-react";
import { api, apiErr } from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const videoUrl = (id) => `${BACKEND}/api/publications/${id}/video`;
const imgUrl = (id, rev) => `${BACKEND}/api/publications/${id}/image${rev ? `?v=${rev}` : ""}`;
const MAX_VIDEO = 120 * 1024 * 1024; // capacité doublée : 120 Mo par vidéo

const fmtDate = (s) => s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [zipping, setZipping] = useState(false);
  const [clearing, setClearing] = useState(false);
  // Dépôt direct d'une vidéo (glisser-déposer ou clic)
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/publications")
      .then((r) => setVideos(r.data.filter((p) => p.kind === "video")))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const stuck = videos.filter((v) => v.generating);

  const clearStuck = async () => {
    setClearing(true);
    try {
      const { data } = await api.post("/admin/publications/clear-stuck-videos");
      toast.success(data.cleared > 0 ? `${data.cleared} vidéo(s) débloquée(s).` : "Aucune vidéo bloquée.");
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setClearing(false); }
  };

  const downloadAll = async () => {
    setZipping(true);
    try {
      const res = await api.get("/admin/blog/videos/download-all", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "aca-videos.zip"; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiErr(err) || "Aucune vidéo à télécharger."); }
    finally { setZipping(false); }
  };

  // Dépôt d'une vidéo : on glisse le fichier, il part aussitôt ; le bloc d'affichage
  // prend automatiquement le format réel de la vidéo (portrait ou paysage).
  const uploadFile = async (file) => {
    if (!file || uploading) return;
    if (!file.type.startsWith("video/")) { toast.error("Choisissez un fichier vidéo (MP4, WebM, MOV…)."); return; }
    if (file.size > MAX_VIDEO) { toast.error("Vidéo trop lourde (max 120 Mo)."); return; }
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/admin/publications/upload-video", fd, {
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded * 100) / e.total)); },
      });
      toast.success("Vidéo déposée — son bloc s'est automatiquement mis à son format.");
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); setProgress(0); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); uploadFile(e.dataTransfer.files?.[0]); };

  // Poser soi-même l'image de couverture (« façade ») d'une vidéo : visible partout
  // (liste du blog, début de l'article) sur tous les navigateurs.
  const setCover = (id) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Choisissez une image (JPG, PNG, WebP)."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop lourde (max 5 Mo)."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.put(`/admin/publications/${id}`, { image: reader.result });
        toast.success("Image de couverture enregistrée — elle s'affiche en façade sur le blog.");
        load();
      } catch (err) { toast.error(apiErr(err)); }
    };
    reader.readAsDataURL(file);
  };

  const ready = videos.filter((v) => v.has_video);

  return (
    <div className="mx-auto max-w-6xl" data-testid="admin-videos">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">Blog · Vidéos IA</div>
          <h1 className="mt-2 font-display text-3xl font-900 text-white">Bibliothèque vidéos</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-secondary">
            Vidéos générées par l'IA ou déposées directement. Téléchargez-les individuellement ou toutes en une archive ZIP.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {stuck.length > 0 && (
            <button onClick={clearStuck} disabled={clearing} data-testid="videos-clear-stuck"
              className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2.5 text-sm font-600 text-amber-300 transition-all hover:border-amber-400 disabled:opacity-60">
              {clearing ? <Loader2 size={15} className="animate-spin" /> : <Unlock size={15} />} Débloquer {stuck.length} vidéo{stuck.length > 1 ? "s" : ""}
            </button>
          )}
          <button onClick={downloadAll} disabled={zipping || ready.length === 0} data-testid="videos-download-all"
            className="inline-flex items-center gap-2 bg-cyan px-4 py-2.5 text-sm font-600 text-void transition-all hover:glow-cyan disabled:opacity-60">
            {zipping ? <Loader2 size={15} className="animate-spin" /> : <Package size={15} />} Tout télécharger (ZIP)
          </button>
        </div>
      </div>

      {/* Section « bibliotheque » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-bibliotheque">Bibliothèque vidéos</div>
      <div data-testid="videos-panel-bibliotheque">
        {/* Zone de dépôt : glisser la vidéo, elle part aussitôt */}
        <div
          role="button" tabIndex={0}
          onClick={() => !uploading && fileRef.current?.click()}
          onKeyDown={(e) => e.key === "Enter" && !uploading && fileRef.current?.click()}
          onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid="video-dropzone"
          className={`mt-8 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-all ${
            dragging ? "border-cyan bg-cyan/10 scale-[1.01]" : "border-white/20 bg-surface hover:border-cyan/50"
          } ${uploading ? "pointer-events-none opacity-80" : ""}`}>
          <input ref={fileRef} type="file" accept="video/*" className="hidden" data-testid="video-drop-input"
            onChange={(e) => uploadFile(e.target.files?.[0])} />
          {uploading ? (
            <>
              <Loader2 size={26} className="animate-spin text-cyan" />
              <span className="text-sm font-600 text-white">Envoi de la vidéo… {progress}%</span>
              <span className="h-1.5 w-64 max-w-full overflow-hidden rounded-full bg-white/10" data-testid="video-upload-progress">
                <span className="block h-full rounded-full bg-cyan transition-all" style={{ width: `${progress}%` }} />
              </span>
            </>
          ) : (
            <>
              <UploadCloud size={30} className={dragging ? "text-cyan" : "text-ink-tertiary"} />
              <span className="text-sm font-600 text-white">
                {dragging ? "Lâchez la vidéo…" : "Glissez votre vidéo ici (ou cliquez pour la choisir)"}
              </span>
              <span className="text-xs text-ink-tertiary">
                MP4, WebM, MOV — max 120 Mo. Le bloc se mettra automatiquement au format de la vidéo.
              </span>
            </>
          )}
        </div>

      {videos.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-white/15 p-16 text-center text-sm text-ink-tertiary">
          Aucune vidéo pour le moment.
        </div>
      ) : (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {videos.map((v) => {
            // Bloc au format réel de la vidéo : portrait (hauteur fixe, centrée) ou paysage (pleine largeur)
            const vw = v.video_w || 16;
            const vh = v.video_h || 9;
            const portrait = v.vertical || vh > vw;
            return (
            <div key={v.id} className="overflow-hidden rounded-2xl border border-white/10 bg-surface" data-testid={`video-item-${v.id}`}>
              <div className={`relative bg-black ${portrait ? "mx-auto h-[380px] max-w-full" : "w-full"}`}
                style={{ aspectRatio: `${vw} / ${vh}` }} data-testid={`video-frame-${v.id}`}>
                {v.has_video
                  ? <video src={videoUrl(v.id)} poster={v.has_image ? imgUrl(v.id, v.image_rev) : undefined} controls playsInline preload="metadata" className="h-full w-full object-contain" />
                  : <div className="flex h-full flex-col items-center justify-center gap-2 text-cyan/50">
                      {v.generating
                        ? <><Loader2 size={26} className="animate-spin" /><span className="text-xs">Génération…</span></>
                        : v.gen_error
                          ? <><AlertTriangle size={26} className="text-red-400" /><span className="text-xs text-red-300">Échec de génération</span></>
                          : <Film size={30} />}
                    </div>}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-wider ${v.status === "published" ? "bg-cyan/15 text-cyan" : "bg-amber-400/15 text-amber-300"}`}>
                    {v.status === "published" ? "Publié" : "En préparation"}
                  </span>
                  <span className="text-[11px] text-ink-tertiary">{fmtDate(v.published_at || v.created_at)}</span>
                  {v.video_w > 0 && (
                    <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-ink-secondary" data-testid={`video-format-${v.id}`}>
                      {portrait ? "Portrait" : "Paysage"} {v.video_w}×{v.video_h}
                    </span>
                  )}
                </div>
                <div className="mt-2 line-clamp-2 font-display text-base font-600 text-white">{v.title}</div>
                {v.gen_error && !v.has_video && (
                  <div className="mt-2 rounded-lg border border-red-400/25 bg-red-400/10 p-2 text-[11px] leading-snug text-red-300" data-testid={`video-error-${v.id}`}>
                    {v.gen_error_detail || "La génération a échoué. Réessayez ou vérifiez le solde de la clé IA."}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-cyan transition-colors hover:underline" data-testid={`video-cover-${v.id}`}>
                    <ImagePlus size={14} /> {v.has_image ? "Changer la couverture" : "Ajouter une couverture"}
                    <input type="file" accept="image/*" className="hidden" onChange={setCover(v.id)} data-testid={`video-cover-input-${v.id}`} />
                  </label>
                  {v.has_video && (
                    <a href={`${videoUrl(v.id)}?download=1`} data-testid={`video-download-${v.id}`}
                      className="inline-flex items-center gap-1.5 text-sm text-cyan hover:underline">
                      <Download size={14} /> Télécharger
                    </a>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
      </div>
      </>
    </div>
  );
}
